<script>
const MINUTE_IN_MILLI_SECONDS = 60000;
const HOUR_IN_MILLI_SECONDS = MINUTE_IN_MILLI_SECONDS * 60;
const DAY_IN_MILLI_SECONDS = HOUR_IN_MILLI_SECONDS * 24;

import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { useExactTimestamp } from 'shared/composables/useExactTimestamp';

const getPreferredLocale = fallbackLocale =>
  window.chatwootConfig?.selectedLocale ||
  document?.documentElement?.lang ||
  fallbackLocale ||
  'es';

export default {
  name: 'TimeAgo',
  props: {
    isAutoRefreshEnabled: {
      type: Boolean,
      default: true,
    },
    lastActivityTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    createdAtTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    conversationId: {
      type: [String, Number],
      default: '',
    },
  },
  setup() {
    return { exactTimestamp: useExactTimestamp() };
  },
  data() {
    const locale = getPreferredLocale(this.$root?.$i18n?.locale);

    return {
      lastActivityAtTimeAgo: dynamicTime(this.lastActivityTimestamp, locale),
      createdAtTimeAgo: dynamicTime(this.createdAtTimestamp, locale),
      timer: null,
    };
  },
  computed: {
    activeLocale() {
      return getPreferredLocale(this.$root?.$i18n?.locale);
    },
    lastActivityTime() {
      return shortTimestamp(
        this.lastActivityAtTimeAgo,
        false,
        this.activeLocale
      );
    },
    createdAtTime() {
      return shortTimestamp(this.createdAtTimeAgo, false, this.activeLocale);
    },
    createdAt() {
      return `${this.$t(
        'CHAT_LIST.CHAT_TIME_STAMP.CREATED.OLDEST'
      )} ${this.exactTimestamp(this.createdAtTimestamp)}`;
    },
    lastActivity() {
      return `${this.$t(
        'CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.NOT_ACTIVE'
      )} ${this.exactTimestamp(this.lastActivityTimestamp)}`;
    },
    tooltipText() {
      return `${this.createdAt}\n${this.lastActivity}`;
    },
  },
  watch: {
    lastActivityTimestamp() {
      this.lastActivityAtTimeAgo = dynamicTime(
        this.lastActivityTimestamp,
        this.activeLocale
      );
    },
    createdAtTimestamp() {
      this.createdAtTimeAgo = dynamicTime(
        this.createdAtTimestamp,
        this.activeLocale
      );
    },
    activeLocale() {
      this.lastActivityAtTimeAgo = dynamicTime(
        this.lastActivityTimestamp,
        this.activeLocale
      );
      this.createdAtTimeAgo = dynamicTime(
        this.createdAtTimestamp,
        this.activeLocale
      );
    },
    conversationId() {
      // Reset display values and timer when the row is recycled to a different conversation.
      this.lastActivityAtTimeAgo = dynamicTime(
        this.lastActivityTimestamp,
        this.activeLocale
      );
      this.createdAtTimeAgo = dynamicTime(
        this.createdAtTimestamp,
        this.activeLocale
      );
      if (this.isAutoRefreshEnabled) {
        clearTimeout(this.timer);
        this.createTimer();
      }
    },
  },
  mounted() {
    if (this.isAutoRefreshEnabled) {
      this.createTimer();
    }
  },
  unmounted() {
    clearTimeout(this.timer);
  },
  methods: {
    createTimer() {
      this.timer = setTimeout(() => {
        this.lastActivityAtTimeAgo = dynamicTime(
          this.lastActivityTimestamp,
          this.activeLocale
        );
        this.createdAtTimeAgo = dynamicTime(
          this.createdAtTimestamp,
          this.activeLocale
        );
        this.createTimer();
      }, this.refreshTime());
    },
    refreshTime() {
      const timeDiff = Date.now() - this.lastActivityTimestamp * 1000;
      if (timeDiff > DAY_IN_MILLI_SECONDS) {
        return DAY_IN_MILLI_SECONDS;
      }
      if (timeDiff > HOUR_IN_MILLI_SECONDS) {
        return HOUR_IN_MILLI_SECONDS;
      }

      return MINUTE_IN_MILLI_SECONDS;
    },
  },
};
</script>

<template>
  <div
    v-tooltip.top="{
      content: tooltipText,
      popperClass: 'whitespace-pre-line',
      delay: { show: 1000, hide: 0 },
    }"
    class="ml-auto leading-4 text-xxs text-n-slate-10 hover:text-n-slate-11"
  >
    <span>{{ lastActivityTime || createdAtTime }}</span>
  </div>
</template>
